import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const formatDateTime = (isoString) => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return 'N/A';
  const start = new Date(startedAt);
  const end = new Date(endedAt);
  const diffSeconds = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
  const minutes = Math.floor(diffSeconds / 60);
  const seconds = diffSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const getPositionColor = (position) => {
  if (position === 1) return 'text-yellow-400 font-bold';
  if (position === 2) return 'text-orange-400 font-bold';
  if (position === 3) return 'text-orange-300 font-bold';
  if (typeof position === 'number' && position <= 10) return 'text-green-400 font-semibold';
  return 'text-gray-300';
};

const getPositionText = (position) => {
  if (!position && position !== 0) return 'Eliminated';
  if (position === 1) return '1st';
  if (position === 2) return '2nd';
  if (position === 3) return '3rd';
  return `${position}th`;
};

const formatItemEffect = (effect) => {
  if (!effect) return null;

  const parts = [];
  if (typeof effect.heal === 'number') parts.push(`Heals ${effect.heal}`);
  if (typeof effect.mana === 'number') parts.push(`Restores ${effect.mana} mana`);
  if (typeof effect.dot === 'number') {
    const ticks = typeof effect.ticks === 'number' ? effect.ticks : '?';
    parts.push(`DOT ${effect.dot} × ${ticks}`);
  }
  if (typeof effect.attackBoost === 'number') {
    const duration = typeof effect.durationSec === 'number' ? `${effect.durationSec}s` : '';
    parts.push(`+${Math.round(effect.attackBoost * 100)}% attack ${duration}`.trim());
  }
  if (typeof effect.defenseBoost === 'number') {
    const duration = typeof effect.durationSec === 'number' ? `${effect.durationSec}s` : '';
    parts.push(`+${Math.round(effect.defenseBoost * 100)}% defense ${duration}`.trim());
  }
  if (typeof effect.speedBoost === 'number') {
    const duration = typeof effect.durationSec === 'number' ? `${effect.durationSec}s` : '';
    parts.push(`+${Math.round(effect.speedBoost * 100)}% speed ${duration}`.trim());
  }

  return parts.length > 0 ? parts.join(', ') : null;
};

const BattleDetail = () => {
  const { id } = useParams();
  const [battle, setBattle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchBattle = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`/api/battles/${id}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'soulwarz'
          }
        });
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Battle not found');
          }
          throw new Error('Failed to load battle details');
        }

        const data = await response.json();
        console.log('data', data);
        if (isMounted) {
          setBattle(data.data);
        }
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError.message || 'Failed to load battle details');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchBattle();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const themeBasePath = useMemo(() => {
    if (!battle?.themeId) return null;
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    return `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${battle.themeId}`;
  }, [battle?.themeId]);

  const resolveItemIconUrl = (iconKey) => {
    if (!iconKey) return null;
    if (typeof iconKey === 'string' && iconKey.startsWith('http')) {
      return iconKey;
    }

    if (!themeBasePath) return null;

    const sanitizedKey = String(iconKey).replace(/^items\//, '');
    const hasExtension = /\.[a-z0-9]+$/i.test(sanitizedKey);
    return `${themeBasePath}/items/${sanitizedKey}${hasExtension ? '' : '.png'}`;
  };

  const renderLoot = (lootEntries) => {
    if (!lootEntries || lootEntries.length === 0) {
      return <span className="text-gray-500 text-xs">No loot</span>;
    }

    const sortedLoot = [...lootEntries].sort((a, b) => {
      const aScore = a.lootType === 'token' ? 0 : 1;
      const bScore = b.lootType === 'token' ? 0 : 1;
      if (aScore !== bScore) return aScore - bScore;

      if (a.lootType === 'token' && b.lootType === 'token') {
        const aSymbol = (a.tokenSymbol || '').toLowerCase();
        const bSymbol = (b.tokenSymbol || '').toLowerCase();
        return aSymbol.localeCompare(bSymbol);
      }

      // Tiers removed - no longer sorting by tier

      const aName = (a.itemName || '').toLowerCase();
      const bName = (b.itemName || '').toLowerCase();
      return aName.localeCompare(bName);
    });

    return (
      <div className="flex flex-wrap gap-2">
        {sortedLoot.map((loot, index) => {
          if (loot.lootType === 'token') {
            return (
              <div
                key={`${loot.lootType}-${index}`}
                className="flex items-center space-x-1 bg-yellow-900/30 px-2 py-1 rounded-md border border-yellow-700/30"
              >
                <span role="img" aria-label="token" className="text-yellow-400">💰</span>
                <span className="text-yellow-200 text-xs">
                  {(loot.amount ?? loot.qty ?? 0).toLocaleString()} ${loot.tokenSymbol || 'SOULWARS'}
                </span>
              </div>
            );
          }

          let reconciledItemId = loot.themeItemId;
          //Trim out the tier suffix from the itemId ie "potion_strength_t4" to "potion_strength"
          reconciledItemId = reconciledItemId.replace(/_t[1-5]$/, '');
          //console.log('reconciledItemId', reconciledItemId);
          const iconUrl = resolveItemIconUrl(reconciledItemId);
          //console.log('iconUrl', iconUrl);
          const itemEffect = formatItemEffect(loot.itemEffect);

          return (
            <div
              key={`${loot.lootType}-${loot.themeItemId || index}`}
              className="flex items-center space-x-2 bg-blue-900/30 px-2 py-1 rounded-md border border-blue-700/30"
              title={[
                loot.itemName || loot.notes || 'Item',
                itemEffect ? `Effect: ${itemEffect}` : null
              ]
                .filter(Boolean)
                .join('\n')}
            >
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt={loot.itemName || 'Item'}
                  className="w-4 h-4"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <span className="text-xs text-blue-200">
                {loot.amount && loot.amount > 1 ? ` ×${loot.amount}` : ''}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const formatTimeAlive = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '—';
    }
    return `${value.toFixed(2)}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 py-10">
        <div className="max-w-6xl mx-auto bg-gray-800 rounded-lg p-6 text-center text-gray-300 shadow-lg">
        Loading battle details...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 py-10">
        <div className="max-w-6xl mx-auto bg-gray-800 rounded-lg p-6 text-center text-red-400 shadow-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!battle) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900 py-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Battle Details</h1>
            <div className="text-gray-400 text-sm">Battle ID: {battle.id}</div>
          </div>
          <div className="flex gap-2">
            <span className="px-3 py-1 rounded-full bg-indigo-600/40 text-indigo-200 text-sm capitalize">
              {battle.mode?.replace(/-/g, ' ') || 'Unknown Mode'}
            </span>
            <span className="px-3 py-1 rounded-full bg-gray-700 text-gray-200 text-sm capitalize">
              Status: {battle.status || 'unknown'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 text-sm text-gray-300">
          <div>
            <div className="text-gray-400">Theme ID</div>
            <div className="font-medium text-white">{battle.themeId || 'N/A'}</div>
          </div>
          <div>
            <div className="text-gray-400">Started At</div>
            <div className="font-medium text-white">{formatDateTime(battle.startedAt)}</div>
          </div>
          <div>
            <div className="text-gray-400">Ended At</div>
            <div className="font-medium text-white">{formatDateTime(battle.endedAt)}</div>
          </div>
          <div>
            <div className="text-gray-400">Duration</div>
            <div className="font-medium text-white">{formatDuration(battle.startedAt, battle.endedAt)}</div>
          </div>
          <div>
            <div className="text-gray-400">Max Players</div>
            <div className="font-medium text-white">{battle.maxPlayers}</div>
          </div>
          <div>
            <div className="text-gray-400">Participants</div>
            <div className="font-medium text-white">{battle.participants?.length || 0}</div>
          </div>
        </div>
      </div>

        <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-white mb-4">Participants</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-600 text-left text-gray-300">
                <th className="py-2 px-3">Placement</th>
                <th className="py-2 px-3">Hero</th>
                <th className="py-2 px-3">Elims</th>
                <th className="py-2 px-3">XP Earned</th>
                <th className="py-2 px-3">Time Alive</th>
                <th className="py-2 px-3">Damage</th>
                <th className="py-2 px-3">Loot</th>
              </tr>
            </thead>
            <tbody>
              {battle.participants?.map(participant => (
                <tr key={participant.id} className="border-b border-gray-700/60 hover:bg-gray-700/30">
                  <td className={`py-3 px-3 ${getPositionColor(participant.finalPosition)}`}>
                    {getPositionText(participant.finalPosition)}
                  </td>
                  <td className="py-3 px-3">
                    {participant.heroId ? (
                      <Link
                        to={`/hero/${participant.heroId}`}
                        className="text-blue-300 hover:text-blue-200 font-medium"
                      >
                        {participant.heroName || participant.heroId}
                      </Link>
                    ) : (
                      <span className="text-gray-300">Unknown Hero</span>
                    )}
                    {participant.heroLevel ? (
                      <div className="text-xs text-gray-400">Level {participant.heroLevel}</div>
                    ) : null}
                  </td>
                  <td className="py-3 px-3 text-gray-200">{participant.eliminationsCount ?? 0}</td>
                  <td className="py-3 px-3 text-yellow-300">{participant.xpEarned?.toLocaleString?.() || participant.xpEarned || 0}</td>
                  <td className="py-3 px-3 text-gray-200">{formatTimeAlive(participant.timeAlive)}</td>
                  <td className="py-3 px-3 text-gray-200">
                    <div className="flex flex-col text-xs text-gray-300">
                      <span>Dealt: {participant.damageDealt ?? 0}</span>
                      <span>Taken: {participant.damageTaken ?? 0}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3">{renderLoot(participant.loot)}</td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BattleDetail;

